import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { BookOpen, ClipboardList, FileText } from 'lucide-react'
import { assignmentsApi } from '../../api/assignments'
import {
  learningMaterialsCatalogApi,
  type LearningMaterialItem,
} from '../../api/learningMaterials'
import { plansApi } from '../../api/plans'
import {
  createEvaluation as createMock,
  getEvaluationTemplates as getMockTemplates,
  type EvaluationTemplate as MockTemplate,
} from '../../api/evaluations'

export type TaskMgrProps = {
  opened: boolean
  onClose: () => void
  /** Study plan this insert applies to. */
  plan_id: number
  /** 0-based or API-defined index where the new session row is inserted. */
  insert_position: number | null
  /** Student who owns the plan (assign mocks / material to this user). */
  target_student_id: number | null
  /** Logged-in user performing the action (authorization / auditing). */
  current_user_id: number
  /** Current user role (e.g. tutor gating). */
  role: string
  /** Optional existing session row this insert is anchored to, if applicable. */
  session_id?: number | null
  /** Invoked after the plan timeline changes so the parent can refetch sessions. */
  onPlanSessionsUpdated?: () => void
  /**
   * Student adds tasks to their own plan (append at end). Uses student assignment / evaluation APIs;
   * no tutor `assign` or tutor-only routes.
   */
  studentSelfServe?: boolean
}

export function TaskMgr({
  opened,
  onClose,
  plan_id,
  insert_position,
  target_student_id,
  current_user_id,
  role: _role,
  session_id: anchorSessionId = null,
  onPlanSessionsUpdated,
  studentSelfServe = false,
}: TaskMgrProps) {
  const navigate = useNavigate()

  const [mockModalOpen, setMockModalOpen] = useState(false)
  const [mockTemplates, setMockTemplates] = useState<MockTemplate[]>([])
  const [mockTitle, setMockTitle] = useState('')
  const [mockTemplateId, setMockTemplateId] = useState<string | null>(null)
  const [creatingMock, setCreatingMock] = useState(false)

  const [learningModalOpen, setLearningModalOpen] = useState(false)
  const [learningMaterials, setLearningMaterials] = useState<LearningMaterialItem[]>([])
  const [loadingLearningList, setLoadingLearningList] = useState(false)
  const [selectedLearning, setSelectedLearning] = useState<LearningMaterialItem | null>(null)
  const [submittingLearning, setSubmittingLearning] = useState(false)

  function handlePickAssignment() {
    if (!studentSelfServe && insert_position === null) return
    if (!current_user_id) {
      notifications.show({ title: 'Error', message: 'You must be signed in', color: 'red' })
      return
    }
    onClose()
    if (studentSelfServe) {
      navigate(`/study-plan/assignments/create?insertPlanId=${plan_id}&append=1`)
      return
    }
    const q = new URLSearchParams({
      insertPlanId: String(plan_id),
      insertPosition: String(insert_position),
    })
    if (anchorSessionId != null) {
      q.set('anchorSessionId', String(anchorSessionId))
    }
    navigate(`/tutor/assignments/create?${q}`)
  }

  function handlePickMockTest() {
    onClose()
    setMockTitle('')
    setMockTemplateId(null)
    setMockModalOpen(true)
    if (mockTemplates.length === 0) {
      getMockTemplates()
        .then((res) => setMockTemplates(res.data))
        .catch(() =>
          notifications.show({ title: 'Error', message: 'Failed to load mock templates', color: 'red' }),
        )
    }
  }

  function handlePickLearning() {
    onClose()
    setSelectedLearning(null)
    setLearningModalOpen(true)
  }

  useEffect(() => {
    if (!learningModalOpen) return
    setLoadingLearningList(true)
    learningMaterialsCatalogApi
      .list({ page: 1, per_page: 200 })
      .then((res) => setLearningMaterials(res.data.items))
      .catch(() =>
        notifications.show({ title: 'Error', message: 'Failed to load learning materials', color: 'red' }),
      )
      .finally(() => setLoadingLearningList(false))
  }, [learningModalOpen])

  async function handleCreateMock() {
    if (!studentSelfServe && insert_position === null) {
      notifications.show({ title: 'Error', message: 'Insert position not set', color: 'red' })
      return
    }
    if (!current_user_id) {
      notifications.show({ title: 'Error', message: 'You must be signed in', color: 'red' })
      return
    }
    if (!studentSelfServe && target_student_id == null) {
      notifications.show({ title: 'Error', message: 'Plan student not yet loaded', color: 'red' })
      return
    }
    if (!mockTitle.trim()) {
      notifications.show({ title: 'Validation', message: 'Title is required', color: 'orange' })
      return
    }
    if (!mockTemplateId) {
      notifications.show({ title: 'Validation', message: 'Select a mock template', color: 'orange' })
      return
    }

    setCreatingMock(true)
    try {
      if (studentSelfServe) {
        await createMock({
          title: mockTitle.trim(),
          evaluation_template_id: Number(mockTemplateId),
          plan_id,
          insert_append: true,
        })
        notifications.show({
          title: 'Mock created',
          message: 'Your mock was added to your study plan.',
          color: 'green',
        })
      } else {
        await createMock({
          title: mockTitle.trim(),
          evaluation_template_id: Number(mockTemplateId),
          student_ids: [target_student_id!],
        })

        await plansApi.insertSession(plan_id, {
          position: insert_position!,
          title: mockTitle.trim(),
          description: 'Mock Test',
          status: 'assigned',
        })

        notifications.show({
          title: 'Mock created',
          message: `Mock assigned to student and inserted at position ${insert_position}.`,
          color: 'green',
        })
      }
      setMockModalOpen(false)
      onPlanSessionsUpdated?.()
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined
      notifications.show({
        title: 'Error',
        message: detail || 'Failed to create mock',
        color: 'red',
      })
    } finally {
      setCreatingMock(false)
    }
  }

  async function handleCreateLearning() {
    if (!studentSelfServe && insert_position === null) {
      notifications.show({ title: 'Error', message: 'Insert position not set', color: 'red' })
      return
    }
    if (!current_user_id) {
      notifications.show({ title: 'Error', message: 'You must be signed in', color: 'red' })
      return
    }
    if (!studentSelfServe && target_student_id == null) {
      notifications.show({ title: 'Error', message: 'Plan student not yet loaded', color: 'red' })
      return
    }
    if (!selectedLearning) {
      notifications.show({ title: 'Pick material', message: 'Select a learning item from the list.', color: 'orange' })
      return
    }

    setSubmittingLearning(true)
    try {
      if (studentSelfServe) {
        await assignmentsApi.create({
          title: selectedLearning.title,
          description: 'Watch learning material',
          action: 'watch',
          assignment_type: 'watch',
          grading_mode: 'auto',
          base_xp: 10,
          route: '/watch',
          action_params: String(selectedLearning.id),
          insert_plan_id: plan_id,
          insert_append: true,
        })
        notifications.show({
          title: 'Added',
          message: 'Watch activity added to your study plan.',
          color: 'green',
        })
      } else {
        const { data: created } = await assignmentsApi.create({
          title: selectedLearning.title,
          description: 'Watch learning material',
          action: 'watch',
          assignment_type: 'watch',
          grading_mode: 'auto',
          base_xp: 10,
          route: '/watch',
          action_params: String(selectedLearning.id),
        })
        await assignmentsApi.assign(created.id, { student_ids: [target_student_id!] })
        await plansApi.insertSession(plan_id, {
          position: insert_position!,
          title: selectedLearning.title,
          description: 'Learning',
          status: 'assigned',
        })
        notifications.show({
          title: 'Assigned',
          message: 'Watch activity added to the plan and student assignments.',
          color: 'green',
        })
      }
      setLearningModalOpen(false)
      setSelectedLearning(null)
      onPlanSessionsUpdated?.()
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined
      notifications.show({
        title: 'Error',
        message: detail || 'Failed to create watch assignment',
        color: 'red',
      })
    } finally {
      setSubmittingLearning(false)
    }
  }

  const pickerTitle = studentSelfServe
    ? 'Add a task to your study plan'
    : `Insert new session at position ${insert_position ?? ''}`
  const mockModalTitle = studentSelfServe
    ? 'Create mock test'
    : `Create Mock Test at position ${insert_position ?? ''}`
  const learningModalTitle = studentSelfServe
    ? 'Choose learning material'
    : `Assign learning material at position ${insert_position ?? ''}`

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={pickerTitle}
        radius="xl"
        size="md"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="var(--sat-text-muted)">
            {studentSelfServe
              ? 'What would you like to add? New items are placed at the end of your plan.'
              : 'What kind of session do you want to insert here?'}
          </Text>
          <SimpleGrid cols={3} spacing="sm">
            <TypePickerCard
              icon={<FileText size={28} />}
              color="orange"
              label="Assignment"
              description={
                studentSelfServe
                  ? 'Practice, quiz, review — opens assignment builder'
                  : 'Practice, quiz, review, etc.'
              }
              onClick={handlePickAssignment}
            />
            <TypePickerCard
              icon={<ClipboardList size={28} />}
              color="grape"
              label="Mock Test"
              description={
                studentSelfServe
                  ? 'Full SAT mock on your study plan timeline'
                  : 'Full SAT mock assigned to the student'
              }
              onClick={handlePickMockTest}
            />
            <TypePickerCard
              icon={<BookOpen size={28} />}
              color="blue"
              label="Learning"
              description="Pick catalog material to watch"
              onClick={handlePickLearning}
            />
          </SimpleGrid>
        </Stack>
      </Modal>

      <Modal
        opened={mockModalOpen}
        onClose={() => setMockModalOpen(false)}
        title={mockModalTitle}
        radius="xl"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="var(--sat-text-muted)">
            {studentSelfServe
              ? 'This mock will be added to the end of your study plan timeline.'
              : "This mock will be assigned to the plan's student and inserted as a new session in the timeline."}
          </Text>
          <TextInput
            label="Title"
            placeholder="e.g. Midterm SAT Mock"
            value={mockTitle}
            onChange={(e) => setMockTitle(e.currentTarget.value)}
            required
            radius="md"
          />
          <Select
            label="Mock Template"
            placeholder="Select a template"
            data={mockTemplates.map((t) => ({ value: String(t.id), label: t.title }))}
            value={mockTemplateId}
            onChange={setMockTemplateId}
            required
            radius="md"
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => setMockModalOpen(false)} radius="xl">
              Cancel
            </Button>
            <Button
              color="grapefruit"
              onClick={handleCreateMock}
              loading={creatingMock}
              radius="xl"
            >
              {studentSelfServe ? 'Create' : 'Create & Insert'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={learningModalOpen}
        onClose={() => setLearningModalOpen(false)}
        title={learningModalTitle}
        radius="xl"
        centered
        size="80%"
        styles={{
          content: {
            maxWidth: '80vw',
            height: '80%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Text size="sm" c="var(--sat-text-muted)">
            {studentSelfServe ? (
              <>
                Pick a catalog item. You&apos;ll open it at{' '}
                <Text span fw={600} component="span">
                  /watch/&lt;id&gt;
                </Text>
                .
              </>
            ) : (
              <>
                Pick a catalog item. The student gets a &quot;watch&quot; assignment that opens{' '}
                <Text span fw={600} component="span">
                  /watch/&lt;id&gt;
                </Text>
                .
              </>
            )}
          </Text>
          {loadingLearningList ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
            </Group>
          ) : (
            <ScrollArea flex={1} miw={0} offsetScrollbars type="scroll">
              <Table
                striped
                highlightOnHover
                withTableBorder
                withColumnBorders
                stickyHeader
                layout="fixed"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '28%' }}>Title</Table.Th>
                    <Table.Th style={{ width: 110 }}>Type</Table.Th>
                    <Table.Th>Media URL</Table.Th>
                    <Table.Th style={{ width: '22%' }}>Mastery</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {learningMaterials.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text size="sm" c="dimmed" ta="center" py="md">
                          No learning materials in the catalog.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {learningMaterials.map((m) => {
                    const active = selectedLearning?.id === m.id
                    return (
                      <Table.Tr
                        key={m.id}
                        onClick={() => setSelectedLearning(m)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: active
                            ? 'var(--mantine-color-blue-light)'
                            : undefined,
                        }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500} lineClamp={2}>
                            {m.title}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color="gray">
                            {m.media_type || '—'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" lineClamp={2} style={{ wordBreak: 'break-all' }}>
                            {m.media_url || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" lineClamp={2}>
                            {m.mastery_name ?? (m.mastery_id != null ? `#${m.mastery_id}` : '—')}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}

          {selectedLearning && (
            <Paper withBorder p="md" radius="md" bg="var(--sat-surface-dim)">
              <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                Selected
              </Text>
              <Text fw={700} size="sm" mb="xs">
                {selectedLearning.title}
              </Text>
              <Group gap="xs" mb={4}>
                <Badge variant="light">{selectedLearning.media_type || 'unknown'}</Badge>
              </Group>
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
                {selectedLearning.media_url || '—'}
              </Text>
            </Paper>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => setLearningModalOpen(false)} radius="xl">
              Cancel
            </Button>
            <Button
              onClick={handleCreateLearning}
              radius="xl"
              loading={submittingLearning}
              disabled={!selectedLearning}
            >
              {studentSelfServe ? 'Add to plan' : 'Assign & insert'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

function TypePickerCard({
  icon,
  color,
  label,
  description,
  onClick,
}: {
  icon: ReactNode
  color: string
  label: string
  description: string
  onClick: () => void
}) {
  return (
    <UnstyledButton onClick={onClick} style={{ display: 'block' }}>
      <Paper
        withBorder
        radius="lg"
        p="md"
        style={{
          cursor: 'pointer',
          height: '100%',
          transition: 'transform 120ms ease-in-out, border-color 120ms ease-in-out',
        }}
        className="sat-type-card"
      >
        <Stack gap={6} align="center" ta="center">
          <ThemeIcon size={48} radius="xl" color={color} variant="light">
            {icon}
          </ThemeIcon>
          <Text fw={700} size="sm" c="var(--sat-text)">
            {label}
          </Text>
          <Text size="xs" c="var(--sat-text-muted)">
            {description}
          </Text>
        </Stack>
      </Paper>
    </UnstyledButton>
  )
}
